import { useAppDispatch, useAppSelector } from 'app/config/store';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { AddWidgetModal } from './addWidgetModal';
import { createDashboard, getDashboard, saveDashboard } from './dashboard.reducer';
import { TimeRange } from './timeRange';
import Toolbar from './toolbar';
import {
  composeSaveDashboardPayload,
  DASHBOARD_MODE,
  getDashboardImage,
  getNewWidgetPosition,
  Widget,
  WidgetDefinition,
} from './utils/utils';
import { MIN_WIDGET_HEIGHT, MIN_WIDGET_HEIGHT_MAP, MIN_WIDGET_WIDTH, MIN_WIDGET_WIDTH_MAP, WidgetGridLayout } from './widget-grid-layout';
import { createWidget, deleteWidget, resetWidgets, setWidgets, updateWidget } from './widget.reducer';
import { toast } from 'react-toastify';

const DashboardUpdate = () => {
  const dispatch = useAppDispatch();

  const [cols, setCols] = useState(12);
  const { id } = useParams<{ id: string }>();
  const widgets = useAppSelector(state => state.widget.widgets);
  const dashboard = useAppSelector(state => state.dashboard.entity);
  const [title, setTitle] = useState<string>('Dashboard');
  const [timeRangeValue, setTimeRangeValue] = useState<TimeRange | undefined>('-7d');
  const [darkTheme, setDarkTheme] = useState<boolean>(false);
  const widgetsRef = useRef<HTMLDivElement>();
  const [breakpoint, setBreakpoint] = useState('lg');
  const [image, setImage] = useState<HTMLCanvasElement>();
  const [isAddWidgetModalOpen, setIsAddWidgetModalOpen] = useState<boolean>(false);
  const [editWidgetItem, setEditWidgetItem] = useState<Widget | null>(null);
  const initialMode: DASHBOARD_MODE = location.pathname.includes('edit') || location.pathname.includes('new') ? 'edit' : 'view';
  const [mode, setMode] = useState<DASHBOARD_MODE>(initialMode);

  useEffect(() => {
    if (id) {
      dispatch(getDashboard({ id }));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (dashboard?.id) {
      dispatch(setWidgets(dashboard.widgets));
      setTitle(dashboard.name || 'Dashboard');
      setTimeRangeValue(dashboard.timeRange);
      setDarkTheme(dashboard.theme === 'dark');
    }
  }, [dashboard?.id]);

  const handleCancel = useCallback(() => {
    setMode('view');
  }, []);

  const getImage = () => {
    getDashboardImage({ widgetsRef, setImage });
  };

  useEffect(() => {
    if (image) {
      const base64Image = image.toDataURL('image/png').split(';base64,')[1];
      if (dashboard && dashboard.id) {
        dispatch(saveDashboard({ ...dashboard, name: title, image: base64Image, widgets, theme: darkTheme ? 'dark' : 'light' })).then(
          (resp: any) => {
            if (resp?.payload?.id) {
              toast.success('Dashboard updated successfully!');
            }
          },
        );
      } else {
        const payload = composeSaveDashboardPayload(
          { description: `New Dashboard - ${new Date().getTime()}`, title, image: base64Image, theme: darkTheme ? 'dark' : 'light' },
          widgets,
        );
        dispatch(createDashboard(payload)).then((resp: any) => {
          if (resp?.payload?.id) {
            toast.success('Dashboard created successfully!');
            setMode('view');
          }
        });
      }
    }
  }, [image]);

  const handleEdit = useCallback(() => {
    setMode('edit');
  }, []);

  const handleSave = useCallback(() => {
    getImage();
  }, [title]);

  const saveTitle = useCallback((newTitle: string) => {
    setTitle(newTitle);
  }, []);

  const handleTimeRangeChange = useCallback((timeRange: TimeRange) => {
    setTimeRangeValue(timeRange);
  }, []);

  const handleDelete = useCallback(() => {
    console.log('Delete clicked');
    // Delete logic
  }, []);

  const handleAdd = useCallback(() => {
    setIsAddWidgetModalOpen(true);
    // Add logic
  }, []);

  const handleThemeChange = useCallback((val: boolean) => {
    setDarkTheme(val);
  }, []);

  const handleAddWidget = (config: WidgetDefinition) => {
    const { xPoint, yPoint } = getNewWidgetPosition(widgets, cols, MIN_WIDGET_WIDTH);
    const newWidget = {
      id: new Date().getTime(), // Unique ID for the widget
      definition: config,
      layout: {
        x: xPoint,
        y: yPoint,
        minW: config?.type === 'map' ? MIN_WIDGET_WIDTH_MAP : MIN_WIDGET_WIDTH,
        minH: config?.type === 'map' ? MIN_WIDGET_HEIGHT_MAP : MIN_WIDGET_HEIGHT,
        width: config?.type === 'map' ? MIN_WIDGET_WIDTH_MAP : MIN_WIDGET_WIDTH,
        height: config?.type === 'map' ? MIN_WIDGET_HEIGHT_MAP : MIN_WIDGET_HEIGHT,
      },
    };

    dispatch(createWidget(newWidget));
    setIsAddWidgetModalOpen(false);
  };

  const handleDeleteWidget = (widget: Widget) => {
    dispatch(deleteWidget(widget));
  };

  const handleBreakPointChange = (b: string, c: number) => {
    setBreakpoint(b);
    setCols(c);
  };

  const handleLayoutChange = (layout: Array<Widget>) => {
    dispatch(setWidgets(layout));
  };

  const handleUpdateWidget = (config: WidgetDefinition) => {
    const updatedWidget = {
      id: editWidgetItem.id,
      definition: config,
      layout: {
        x: editWidgetItem.layout.x,
        y: editWidgetItem.layout.y,
        minW: MIN_WIDGET_WIDTH,
        minH: MIN_WIDGET_HEIGHT,
        width: editWidgetItem.layout.width,
        height: editWidgetItem.layout.height,
      },
    };

    dispatch(updateWidget(updatedWidget));
    setIsAddWidgetModalOpen(false);
    setEditWidgetItem(null);
  };

  return (
    <div className="dashboard-body">
      {isAddWidgetModalOpen && (
        <AddWidgetModal
          widget={editWidgetItem}
          timeRange={timeRangeValue}
          onCancel={() => {
            setIsAddWidgetModalOpen(false);
            setEditWidgetItem(null);
          }}
          open={isAddWidgetModalOpen}
          onComplete={(config: WidgetDefinition) => {
            if (editWidgetItem) {
              handleUpdateWidget(config);
            } else {
              handleAddWidget(config);
            }
            setEditWidgetItem(null);
          }}
        />
      )}
      <div className={mode === 'edit' ? 'edit-mode' : ''}>
        <Toolbar
          mode={mode}
          title={title}
          timeRangeValue={timeRangeValue}
          onCancel={handleCancel}
          onEdit={handleEdit}
          onSave={handleSave}
          saveTitle={saveTitle}
          onTimeRangeChange={handleTimeRangeChange}
          onDelete={handleDelete}
          onAdd={handleAdd}
          onChangeTheme={handleThemeChange}
          loggedInUser={null}
          darkTheme={darkTheme}
        />
        <WidgetGridLayout
          theme={darkTheme ? 'dark' : 'light'}
          timeRange={timeRangeValue as any}
          ref={widgetsRef}
          widgets={widgets}
          onDeleteWidget={handleDeleteWidget}
          onLayoutChange={handleLayoutChange}
          onUpdateWidget={handleUpdateWidget}
          onBreakpointChange={handleBreakPointChange}
          setEditWidgetItem={setEditWidgetItem}
          setIsAddWidgetModalOpen={setIsAddWidgetModalOpen}
          mode={mode}
        />
      </div>
    </div>
  );
};

export default DashboardUpdate;
